import { useState, useEffect, type ReactNode } from 'react'
import { Button } from 'components/Button'

type Provider = { id: string | number; name: string }

type ResultItem = {
  key: string
  name: string
  id: string
  status: 'success' | 'failed'
  statusLabel: string
  error?: string
}

type ResultSummary = {
  badges: { label: string; count: number; color: 'green' | 'gray' | 'red' }[]
  items: ResultItem[]
}

type CrawlWizardConfig<P extends Provider> = {
  title: string
  cookieDescription: ReactNode
  cookiePlaceholder: string
  providerSelectLabel: string
  processingLabel: string
  fetchProviders: (cookie: string) => Promise<P[]>
  execute: (cookie: string, providerId: P['id']) => Promise<ResultSummary>
  onSuccess?: () => void
}

type Props<P extends Provider> = {
  open: boolean
  onClose: () => void
  config: CrawlWizardConfig<P>
}

export function CrawlWizardModal<P extends Provider>({ open, onClose, config }: Props<P>) {
  const [step, setStep] = useState<'cookie' | 'provider' | 'result'>('cookie')
  const [cookie, setCookie] = useState('')
  const [providers, setProviders] = useState<P[]>([])
  const [result, setResult] = useState<ResultSummary | null>(null)
  const [error, setError] = useState('')
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleFetchProviders = async () => {
    setError('')
    setIsLoadingProviders(true)
    try {
      const data = await config.fetchProviders(cookie.trim())
      setProviders(data)
      setStep('provider')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(msg || 'Cookieが無効です。再ログインしてください。')
    } finally {
      setIsLoadingProviders(false)
    }
  }

  const handleExecute = async (providerId: P['id']) => {
    setError('')
    setIsProcessing(true)
    try {
      const data = await config.execute(cookie.trim(), providerId)
      setResult(data)
      setStep('result')
      config.onSuccess?.()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(msg || '処理に失敗しました。')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    setStep('cookie')
    setCookie('')
    setProviders([])
    setResult(null)
    setError('')
    onClose()
  }

  useEffect(() => {
    if (!isProcessing && !isLoadingProviders) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isProcessing, isLoadingProviders])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-ink mb-4">{config.title}</h2>

        {step === 'cookie' && (
          <>
            {config.cookieDescription}
            <label className="block text-xs text-muted mb-1">Cookie</label>
            <textarea
              value={cookie}
              onChange={(e) => setCookie(e.target.value)}
              placeholder={config.cookiePlaceholder}
              className="w-full rounded-xl border border-border/70 bg-surface p-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              rows={3}
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={handleClose}>キャンセル</Button>
              <Button size="sm" onClick={handleFetchProviders} disabled={!cookie.trim() || isLoadingProviders} loading={isLoadingProviders}>
                次へ
              </Button>
            </div>
          </>
        )}

        {step === 'provider' && (
          <>
            <p className="text-sm text-muted mb-4">{config.providerSelectLabel}</p>
            {providers.length === 0 && (
              <p className="text-sm text-muted py-4 text-center">プロバイダーが見つかりません。</p>
            )}
            <div className="flex flex-col gap-2">
              {providers.map((p) => (
                <button
                  key={String(p.id)}
                  type="button"
                  onClick={() => handleExecute(p.id)}
                  disabled={isProcessing}
                  className="flex items-center justify-between rounded-xl border border-border/70 bg-white px-4 py-3 text-left text-sm transition hover:bg-surface disabled:opacity-50"
                >
                  <span className="font-semibold text-ink">{p.name}</span>
                  <span className="text-xs text-muted">ID: {String(p.id)}</span>
                </button>
              ))}
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {isProcessing && <p className="mt-3 text-sm text-muted">{config.processingLabel}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={() => setStep('cookie')} disabled={isProcessing}>戻る</Button>
              <Button variant="outline" size="sm" onClick={handleClose} disabled={isProcessing}>キャンセル</Button>
            </div>
          </>
        )}

        {step === 'result' && result && (
          <>
            <div className="mb-4 flex gap-4 text-sm">
              {result.badges.map((b) => (
                <span key={b.label} className={`rounded-full px-3 py-1 ${
                  b.color === 'green' ? 'bg-green-100 text-green-800' :
                  b.color === 'red' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {b.label}: {b.count}
                </span>
              ))}
            </div>
            <div className="max-h-60 overflow-y-auto rounded-xl border border-border/70">
              <table className="w-full text-xs">
                <thead className="bg-surface/70 text-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">名前</th>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">結果</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((item) => (
                    <tr key={item.key} className="border-t border-border/60">
                      <td className="px-3 py-2">{item.name}</td>
                      <td className="px-3 py-2">{item.id || '-'}</td>
                      <td className="px-3 py-2">
                        {item.status === 'success' ? (
                          <span className="text-green-600">{item.statusLabel}</span>
                        ) : (
                          <span className="text-red-600">{item.error || item.statusLabel}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={handleClose}>閉じる</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
